import {BaseCommand} from './base.command';
import {EdgeControlPointSnapshot, MoveSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom, of} from 'rxjs';
import {catchError, defaultIfEmpty} from 'rxjs/operators';
import {UndoLabelMetadata, UndoObjectType} from '../undo-label.utils';

interface MoveCommandData {
  moves: MoveSnapshot[];
  edgesBefore: EdgeControlPointSnapshot[];
  edgesAfter: EdgeControlPointSnapshot[];
}

/**
 * MoveCommand handles position changes for nodes.
 * Supports multi-selection moves as a single atomic operation.
 * Also captures connected edge control points to properly restore curved/segmented edges.
 */
export class MoveCommand extends BaseCommand<MoveCommandData> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    moves: MoveSnapshot[],
    edgesBefore: EdgeControlPointSnapshot[] = [],
    edgesAfter: EdgeControlPointSnapshot[] = []
  ) {
    const count = moves.length;
    // Determine object type based on count
    const objectType: UndoObjectType = count === 1 ? 'Node' : 'Selection';
    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: 'Change',
      part: 'Position',
      elementCount: objectType === 'Selection' ? count : undefined
    };

    super(
      'move',
      'Position Change', // fallback
      {
        before: {moves, edgesBefore, edgesAfter},
        after: {moves, edgesBefore, edgesAfter}
      },
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    // Called on redo - restore positions to "after" state
    const cy = this.cyService.cy;
    if (!cy) return;

    const {moves, edgesAfter} = this.snapshot.after;
    const edgesToReinit: any[] = [];

    cy.batch(() => {
      // Restore node positions
      moves.forEach(move => {
        const node = cy.$(`#${move.nodeId}`);
        if (node.length > 0) {
          node.position(move.after);
        }
      });

      // Restore edge control points
      edgesAfter.forEach(edgeSnapshot => {
        const edge = cy.$(`#${edgeSnapshot.edgeId}`);
        if (edge.length > 0) {
          this.applyEdgeControlPoints(edge, edgeSnapshot);
          edgesToReinit.push(edge);
        }
      });
    });

    // Reinitialize anchor points for edges that had control points restored
    this.reinitializeEdgeAnchors(cy, edgesToReinit);

    // Update positions on backend
    const nodeUpdatePromises = moves.map(move =>
      firstValueFrom(
        this.nodeService.updateNodePosition(move.nodeId, move.after.x, move.after.y).pipe(
          defaultIfEmpty(null),
          catchError(() => of(null))
        )
      )
    );

    // Update edge control points on backend
    const edgeUpdatePromises = edgesAfter.map(edgeSnapshot =>
      this.updateEdgeOnBackend(edgeSnapshot)
    );

    await Promise.all([...nodeUpdatePromises, ...edgeUpdatePromises]);
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {moves, edgesBefore} = this.snapshot.before;
    const edgesToReinit: any[] = [];

    cy.batch(() => {
      // Restore node positions
      moves.forEach(move => {
        const node = cy.$(`#${move.nodeId}`);
        if (node.length > 0) {
          node.position(move.before);
        }
      });

      // Restore edge control points
      edgesBefore.forEach(edgeSnapshot => {
        const edge = cy.$(`#${edgeSnapshot.edgeId}`);
        if (edge.length > 0) {
          this.applyEdgeControlPoints(edge, edgeSnapshot);
          edgesToReinit.push(edge);
        }
      });
    });

    // Reinitialize anchor points for edges that had control points restored
    this.reinitializeEdgeAnchors(cy, edgesToReinit);

    // Update positions on backend
    const nodeUpdatePromises = moves.map(move =>
      firstValueFrom(
        this.nodeService.updateNodePosition(move.nodeId, move.before.x, move.before.y).pipe(
          defaultIfEmpty(null),
          catchError(() => of(null))
        )
      )
    );

    // Update edge control points on backend
    const edgeUpdatePromises = edgesBefore.map(edgeSnapshot =>
      this.updateEdgeOnBackend(edgeSnapshot)
    );

    await Promise.all([...nodeUpdatePromises, ...edgeUpdatePromises]);
  }

  private applyEdgeControlPoints(edge: any, snapshot: EdgeControlPointSnapshot): void {
    // Always set all properties - use null/undefined to clear them
    if (snapshot.bendPointPositions !== undefined) {
      edge.data('bendPointPositions', snapshot.bendPointPositions);
    }
    if (snapshot.controlPointPositions !== undefined) {
      edge.data('controlPointPositions', snapshot.controlPointPositions);
    }
    if (snapshot.cyedgebendeditingDistances !== undefined) {
      edge.data('cyedgebendeditingDistances', snapshot.cyedgebendeditingDistances);
    }
    if (snapshot.cyedgebendeditingWeights !== undefined) {
      edge.data('cyedgebendeditingWeights', snapshot.cyedgebendeditingWeights);
    }
    if (snapshot.cyedgecontroleditingDistances !== undefined) {
      edge.data('cyedgecontroleditingDistances', snapshot.cyedgecontroleditingDistances);
    }
    if (snapshot.cyedgecontroleditingWeights !== undefined) {
      edge.data('cyedgecontroleditingWeights', snapshot.cyedgecontroleditingWeights);
    }
  }

  /**
   * Reinitialize edge anchor points using the edge-editing plugin.
   * This is necessary to make the plugin pick up restored control point data.
   */
  private reinitializeEdgeAnchors(cy: any, edges: any[]): void {
    if (edges.length === 0) return;

    try {
      const edgeEditingInstance = cy.edgeEditing?.();
      if (edgeEditingInstance?.initAnchorPoints) {
        edgeEditingInstance.initAnchorPoints(edges);
      }
    } catch (err) {
      console.warn('MoveCommand: Failed to reinitialize edge anchors', err);
    }
  }

  private async updateEdgeOnBackend(snapshot: EdgeControlPointSnapshot): Promise<void> {
    const payload: any = {
      id: snapshot.edgeId,
      bendPointPositions: snapshot.bendPointPositions,
      controlPointPositions: snapshot.controlPointPositions,
      cyedgebendeditingDistances: snapshot.cyedgebendeditingDistances,
      cyedgebendeditingWeights: snapshot.cyedgebendeditingWeights,
      cyedgecontroleditingDistances: snapshot.cyedgecontroleditingDistances,
      cyedgecontroleditingWeights: snapshot.cyedgecontroleditingWeights
    };

    try {
      await firstValueFrom(
        this.nodeService.updateLink(payload).pipe(
          defaultIfEmpty(null),
          catchError(err => {
            console.warn('MoveCommand: Failed to update edge control points', snapshot.edgeId, err);
            return of(null);
          })
        )
      );
    } catch {
      // Ignore errors
    }
  }
}
